import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'

// Home's figure tile and attention pill. Each is a BUTTON only when it is
// handed a tap — and Home hands one only when the route gate would open the
// page (tapIfOpenable, F16 UF-03/UF-05). Without one it renders as plain
// text: a link that bounces the reader back to Home is worse than none.

export function StatTile({
  label, value, sub, accent, onClick,
}: {
  label: string; value: number | string; sub?: string; accent?: string
  onClick?: () => void
}) {
  const content = (
    <>
      <p className="text-xs text-muted uppercase tracking-wider">{label}</p>
      <p className={clsx('text-2xl font-bold mt-1 tabular-nums', accent ?? 'text-fg')}>{value}</p>
      {sub && <p className="text-xs text-muted mt-0.5">{sub}</p>}
    </>
  )
  const base = 'surface rounded-2xl p-3 text-center'
  return onClick
    ? <button onClick={onClick} className={clsx(base, 'active:scale-[0.98] transition-transform w-full')}>{content}</button>
    : <div className={base}>{content}</div>
}

export function AlertPill({
  icon: Icon, count, label, onClick, tone,
}: {
  icon: LucideIcon
  /** A number, or "—" when the read behind it failed or is still loading. */
  count: number | string
  label: string
  /** Omitted when the reader can't open the page (F16 UF-03): the pill
   *  still reads, it just isn't a button that bounces them back to Home. */
  onClick?: () => void
  tone: 'amber' | 'red' | 'blue' | 'sky'
}) {
  const toneClasses = {
    amber: 'bg-amber-900/40 border-amber-700 text-amber-200',
    red:   'bg-red-900/40   border-red-700   text-red-200',
    blue:  'bg-blue-900/40  border-blue-700  text-blue-200',
    sky:   'bg-sky-900/40   border-sky-700   text-sky-200',
  }[tone]
  const hoverClasses = {
    amber: 'hover:bg-amber-900/60',
    red:   'hover:bg-red-900/60',
    blue:  'hover:bg-blue-900/60',
    sky:   'hover:bg-sky-900/60',
  }[tone]
  const base = 'rounded-xl border px-3 py-2 text-left flex items-center gap-2'
  const content = (
    <>
      <Icon size={16} className="shrink-0" />
      <span className="text-sm">
        <span className="font-bold tabular-nums">{count}</span>{' '}
        <span className="opacity-90">{label}</span>
      </span>
    </>
  )
  return onClick
    ? (
      <button
        onClick={onClick}
        className={clsx(base, 'transition-colors', toneClasses, hoverClasses)}
      >
        {content}
      </button>
    )
    : <div className={clsx(base, toneClasses)}>{content}</div>
}
